'use client';

import { c as _c } from "react/compiler-runtime";
import { ControlBarMoreAction, ControlBarReportAction } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActions';
import type { ControlBarViewportLayoutProps } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarViewportLayout';
import ControlBarExitIconButton from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarExitIconButton';
import Full3dExitButton from '@/app/[locale]/(worlds)/[roomId]/components/Full3dExitButton';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useIsEmbeddedControlBar } from '@/context/EmbeddedControlBarContext';
import { useUAContext } from '@/context/UAContext';
import { exitFullscreen } from '@/lib/fullscreen';
import { cn } from '@/lib/utils';
import type { RecommendRoom } from '@/services/cms';
import { useTranslations } from 'next-intl';
type ControlBarMobileViewportOptions = {
  isMobileLandscape: boolean;
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
};
export function MobileControlBar(t0) {
  const $ = _c(24);
  const {
    moreRecommendationsPromise,
    popupContainer
  } = t0;
  const t = useTranslations();
  const embedded = useIsEmbeddedControlBar();
  const {
    isMobileLandscape
  } = useUAContext();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const contentKind = roomData.content_type?.code === "games" ? "games" : "experiences";
  const t1 = isMobileLandscape ? "landscape" : "portrait";
  const t2 = isMobileLandscape ? "z-floating bg-vive-primary-80/80 flex h-full w-[60px] shrink-0 flex-col justify-between pt-1 pr-2 pb-[max(4px,env(safe-area-inset-bottom))] pl-7 backdrop-blur-2xl" : "z-floating bg-vive-primary-80/80 flex h-[34px] w-full shrink-0 items-center justify-between px-9 py-1 backdrop-blur-2xl";
  const t3 = isMobileLandscape ? "flex flex-col items-center gap-3" : "flex w-[191px] items-center gap-3";
  let t4;
  if ($[0] !== embedded || $[1] !== isMobileLandscape || $[2] !== popupContainer || $[3] !== t) {
    t4 = embedded ? <ControlBarExitIconButton ariaLabel={t("control-bar.exit-fullscreen")} buttonClassName="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/15 p-1 backdrop-blur-[50px]" className="z-floating relative" dataAction="exit-fullscreen" iconClassName={cn(isMobileLandscape && "-rotate-90", "size-3.5 text-vive-primary-20")} onClick={_temp} /> : <Full3dExitButton className="z-floating relative" buttonClassName="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/15 p-1 backdrop-blur-[50px]" dialogContainer={popupContainer} dialogPresentation="mobile-control-bar" iconClassName={`${isMobileLandscape ? "-rotate-90" : ""} size-3.5 text-vive-primary-20`} />;
    $[0] = embedded;
    $[1] = isMobileLandscape;
    $[2] = popupContainer;
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isMobileLandscape ? "flex h-11 w-6 items-center justify-center" : "inline-flex shrink-0";
  const t6 = isMobileLandscape ? "h-6 w-11 -rotate-90 border px-1 py-1 text-[10px]" : "h-6 w-11 border px-1 py-1 text-[10px]";
  let t7;
  if ($[5] !== contentKind || $[6] !== moreRecommendationsPromise || $[7] !== popupContainer || $[8] !== t5 || $[9] !== t6) {
    t7 = <ControlBarMoreAction contentKind={contentKind} moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} buttonWrapperClassName={t5} buttonClassName={t6} showIcon={false} />;
    $[5] = contentKind;
    $[6] = moreRecommendationsPromise;
    $[7] = popupContainer;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== embedded || $[16] !== isMobileLandscape || $[17] !== popupContainer) {
    t9 = !embedded && <ControlBarReportAction className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/15 p-1 backdrop-blur-[50px]" iconClassName={`${isMobileLandscape ? "-rotate-90" : ""} size-4 shrink-0 text-vive-primary-20`} popupContainer={popupContainer} reportModalContainer={popupContainer} />;
    $[15] = embedded;
    $[16] = isMobileLandscape;
    $[17] = popupContainer;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t8 || $[22] !== t9) {
    t10 = <div data-component="mobile-control-bar" data-orientation={t1} className={t2}>{t8}{t9}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp() {
  exitFullscreen();
}
export function createMobileControlBarViewportLayout({
  isMobileLandscape,
  moreRecommendationsPromise
}: ControlBarMobileViewportOptions): Omit<ControlBarViewportLayoutProps, 'children'> {
  return {
    barPlacement: 'before',
    className: isMobileLandscape ? 'flex h-full min-h-0 min-w-0 flex-1 flex-row' : 'flex h-full min-h-0 min-w-0 flex-1 flex-col',
    dataComponent: 'mobile-control-bar-layout',
    dataOrientation: isMobileLandscape ? 'landscape' : 'portrait',
    renderControlBar: popupContainer => <MobileControlBar moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} />,
    viewportDataComponent: 'mobile-control-bar-viewport'
  };
}